import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { TELEMETRY_INITIALIZER } from '@mri-platform/angular-app-insights';
import {
  HttpClientBlobErrorInterceptor,
  HttpClientErrorEnrichmentInterceptor,
  sanitizeHttpClientProblemDetails
} from '@mri-platform/angular-error-handling';
import { provideEntityState } from '@mri-platform/dsg/entity-state';
import { AppMainMenuItemService } from '@mri-platform/dsg/shell';
import {
  defaultBreezeEntityConfig,
  provideAppBreezeIntegration,
  sanitizeBreezeClientSaveError,
  sanitizeBreezeServerError
} from '@mri-platform/shared/app-breeze-integration';
import {
  ApiVersionInterceptor,
  AppUserPermissionService,
  ImpersonateClientSwitchService,
  provideAppCoreIntegration
} from '@mri-platform/shared/app-core-integration';
import { provideAppInsightsIntegration } from '@mri-platform/shared/app-insights-integration';
import { provideNavigationState } from '@mri-platform/shared/app-ngrx-integration';
import { ClientSwitchService, environment, provideSharedCore } from '@mri-platform/shared/core';
import { provideShell } from '@mri-platform/shared/shell';
import { UserPermissionService } from '@mri/angular-wfe-proxy-oidc';
import { provideBreeze } from '@mri/breeze-entity';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { routes } from './app.routes';
import {
  DashboardPageViewTelemetryInitializer,
  FeatureFlagsConfigProvider,
  ignoreIframeError,
  provideIntelligenceApiConfig,
  providePendo
} from './integration';
import { ROOT_REDUCERS } from './reducers';
import { testingProviders } from './testing';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({
      // critical: as of angular v18.2.8, we need to disable event coalescing. Review this decision once upgraded
      // to angular v19+, as event coalescing is desirable for performance reasons.
      // We have to disable coalescing in this angular version to avoid NgModel binding to occur
      // late in the NgForm component lifecycle when it's hosting component is being torn down. This in turn causes
      // the breeze entity to receive null values for properties that are bound to the NgModel during teardown.
      // To verify event coalescing behaviour in the future, open `ClientOnboardConfigurationPageComponent` then:
      // 1. click edit on the client name, but do NOT make a change to the input
      // 2. click undo button alongside the client name input
      // If event coalescing is still a problem, the Save button at the bottom of the page will be ENABLED
      // A possible alternative to disabling event coalescing is to try and somehow block the NgModel binding when
      // the NgForm component is being torn down so that the breeze entity does not receive null values.
      // For more discussion on angular's use of event coalescing, see: https://github.com/angular/angular/issues/57528
      eventCoalescing: false
    }),
    provideRouter(routes, withEnabledBlockingInitialNavigation()),
    provideAnimations(),
    provideShell({ mainMenuItemService: AppMainMenuItemService }),
    provideStore(ROOT_REDUCERS, {
      metaReducers: !environment.production ? [] : [],
      runtimeChecks: {
        strictActionImmutability: false,
        strictStateImmutability: true,
        strictStateSerializability: true
      }
    }),
    provideEffects([]),
    !environment.production ? provideStoreDevtools({ connectInZone: true }) : [],
    provideAppInsightsIntegration(),
    provideBreeze(defaultBreezeEntityConfig),
    provideAppCoreIntegration({
      errorHandling: {
        sanitizers: [sanitizeHttpClientProblemDetails, sanitizeBreezeServerError, sanitizeBreezeClientSaveError]
      },
      errorLogging: { selectors: [ignoreIframeError] }
    }),
    provideAppBreezeIntegration(),
    provideNavigationState(),
    provideEntityState(),
    providePendo(),
    provideIntelligenceApiConfig(),
    provideHttpClient(withInterceptorsFromDi()),
    provideSharedCore({ useFeatureFlags: true }),
    testingProviders,
    { provide: ClientSwitchService, useClass: ImpersonateClientSwitchService },
    { provide: UserPermissionService, useClass: AppUserPermissionService },
    { provide: HTTP_INTERCEPTORS, useClass: HttpClientErrorEnrichmentInterceptor, multi: true },
    // critical: interceptors that handle errors must appear AFTER HttpClientErrorEnrichmentInterceptor
    { provide: HTTP_INTERCEPTORS, useClass: ApiVersionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpClientBlobErrorInterceptor, multi: true },
    { provide: TELEMETRY_INITIALIZER, useClass: DashboardPageViewTelemetryInitializer, multi: true },
    FeatureFlagsConfigProvider
  ]
};
